import {
  ApiDataSourceResponse,
  GroupNode,
  Source,
  SourceGroup,
} from '@highimpact-frontend/api-types';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

type FilterDefinition = {
  key: string;
  label: string;
};

type FilterOption = {
  key: string;
  label: string;
  value: string;
};

type AxisContext = {
  key: string;
  label: string;
};

type FlatSourceRecord = {
  source: Source;
  dimensions: Record<string, string>;
  axis?: AxisContext;
};

function isGroupNode(node: SourceGroup): node is GroupNode {
  return node.kind === 'group';
}

function sourceKey(source: Source) {
  return `${source.measure_point_id}:${source.measure_type_id}`;
}

function uniqueValues<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function normalizeToken(value: string) {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const GROUP_LABEL_FR: Record<string, string> = {
  project: 'projet',
  site: 'site',
  device_type: 'type de capteur',
  device: 'capteur',
  unit: 'unité',
  thematic: 'thématique',
  physical_quantity: 'grandeur physique',
  physical_size: 'grandeur physique',
  timezone: 'fuseau horaire',
};

const AXIS_LABEL_FR: Record<string, string> = {
  unit_x: 'abscisse',
  unit_y: 'ordonnée',
  unit_z: 'axe z',
  unit_theta: 'angle',
  unit_rho: 'rayon',
  unit_phi: 'axe phi',
};

function toFrenchGroupLabel(rawLabel: string, key?: string) {
  const normalizedKey = key ? normalizeToken(key) : '';
  const normalizedLabel = normalizeToken(rawLabel);

  return (
    GROUP_LABEL_FR[normalizedKey] ?? GROUP_LABEL_FR[normalizedLabel] ?? rawLabel
  );
}

function toFrenchAxisLabel(axisKey: string, rawLabel: string) {
  const normalizedKey = normalizeToken(axisKey);
  const normalizedLabel = normalizeToken(rawLabel);

  return (
    AXIS_LABEL_FR[normalizedKey] ?? AXIS_LABEL_FR[normalizedLabel] ?? rawLabel
  );
}

function getIdPrefix(id: string) {
  return id.split(/[:_]/)[0]?.trim() ?? '';
}

function getGroupChildren(group: GroupNode) {
  return (group.children ?? []).filter(isGroupNode);
}

function getCommonGroupPrefix(groups: GroupNode[]) {
  const prefixes = uniqueValues(
    groups.map((group) => getIdPrefix(group.id)).filter(Boolean)
  );
  return prefixes.length === 1 ? prefixes[0] : undefined;
}

function inferDimensionFromGroup(
  group: GroupNode
): FilterDefinition | undefined {
  const children = getGroupChildren(group);

  if (children.length === 0) {
    return undefined;
  }

  if (group.id.startsWith('group:')) {
    const explicitName = group.id.slice('group:'.length).trim() || group.label;
    return {
      key: normalizeToken(explicitName),
      label: group.label.trim() || explicitName,
    };
  }

  const commonPrefix = getCommonGroupPrefix(children);
  if (!commonPrefix) {
    return undefined;
  }

  return {
    key: normalizeToken(commonPrefix),
    label: commonPrefix,
  };
}

export function collectFlatSources(root: SourceGroup): {
  filters: FilterDefinition[];
  records: FlatSourceRecord[];
} {
  const filters: FilterDefinition[] = [];
  const records: FlatSourceRecord[] = [];

  function registerFilter(filter: FilterDefinition) {
    if (!filters.some((existing) => existing.key === filter.key)) {
      filters.push(filter);
    }
  }

  function processChildren(
    children: SourceGroup[],
    dimensions: Record<string, string>,
    axis?: AxisContext
  ) {
    const groupChildren = children.filter(isGroupNode);
    const nonGroupChildren = children.filter(
      (child): child is Exclude<SourceGroup, GroupNode> =>
        child.kind !== 'group'
    );

    const explicitDimensionChildren = groupChildren.filter(
      (child) => child.id.startsWith('group:') || inferDimensionFromGroup(child)
    );

    if (explicitDimensionChildren.length > 0) {
      explicitDimensionChildren.forEach((child) =>
        visit(child, dimensions, undefined, axis)
      );
      nonGroupChildren.forEach((child) =>
        visit(child, dimensions, undefined, axis)
      );
      return;
    }

    const commonPrefix = getCommonGroupPrefix(groupChildren);
    if (commonPrefix) {
      const inferredFilter = {
        key: normalizeToken(commonPrefix),
        label: commonPrefix,
      };
      registerFilter(inferredFilter);
      groupChildren.forEach((child) =>
        visit(child, dimensions, inferredFilter, axis)
      );
      nonGroupChildren.forEach((child) =>
        visit(child, dimensions, undefined, axis)
      );
      return;
    }

    children.forEach((child) => visit(child, dimensions, undefined, axis));
  }

  function visit(
    node: SourceGroup,
    dimensions: Record<string, string>,
    pendingFilter?: FilterDefinition,
    axis?: AxisContext
  ) {
    if (node.kind === 'sources') {
      node.sources.forEach((source) => {
        const sourceDimensions = source.timezone
          ? { ...dimensions, timezone: source.timezone }
          : dimensions;
        records.push({ source, dimensions: sourceDimensions, axis });
      });
      return;
    }

    if (node.kind === 'axis_group') {
      Object.entries(node.axes).forEach(([axisKey, child]) => {
        const axisLabel =
          child.kind === 'sources' ? child.display_name : axisKey;
        visit(child, dimensions, pendingFilter, {
          key: axisKey,
          label: axisLabel,
        });
      });
      return;
    }

    if (pendingFilter) {
      processChildren(
        node.children ?? [],
        { ...dimensions, [pendingFilter.key]: node.label },
        axis
      );
      return;
    }

    const inferredDimension = inferDimensionFromGroup(node);
    if (inferredDimension) {
      registerFilter(inferredDimension);
      getGroupChildren(node).forEach((child) =>
        visit(child, dimensions, inferredDimension, axis)
      );

      (node.children ?? [])
        .filter((child) => child.kind !== 'group')
        .forEach((child) => visit(child, dimensions, undefined, axis));
      return;
    }

    processChildren(node.children ?? [], dimensions, axis);
  }

  visit(root, {});

  if (records.some((record) => record.source.timezone)) {
    registerFilter({ key: 'timezone', label: 'timezone' });
  }

  return { filters, records };
}

type AxisSourcesResult =
  | { kind: 'unique'; sources: Source[] }
  | {
      kind: 'axes';
      axes: Record<string, { label: string; options: Source[] }>;
    };

function buildAxisSourcesResult(
  records: FlatSourceRecord[]
): AxisSourcesResult {
  const hasAssignedAxis = records.some((record) => record.axis !== undefined);

  if (!hasAssignedAxis) {
    return {
      kind: 'unique',
      sources: uniqueValues(records.map((record) => record.source)),
    };
  }

  const axes: Record<string, { label: string; options: Source[] }> = {};

  records.forEach((record) => {
    const axisKey = record.axis?.key ?? 'unassigned';
    const axisLabel = toFrenchAxisLabel(
      axisKey,
      record.axis?.label ?? 'Sans axe'
    );

    axes[axisKey] = axes[axisKey] ?? {
      label: axisLabel,
      options: [],
    };

    if (
      !axes[axisKey].options.some(
        (source) => sourceKey(source) === sourceKey(record.source)
      )
    ) {
      axes[axisKey].options.push(record.source);
    }
  });

  return { kind: 'axes', axes };
}

function matchesSelectedFilters(
  record: FlatSourceRecord,
  filters: Record<string, string[]>,
  definitions: FilterDefinition[]
) {
  return definitions.every((definition) => {
    const selected = filters[definition.key] ?? [];
    if (selected.length === 0) {
      return true;
    }

    return selected.includes(record.dimensions[definition.key] ?? '');
  });
}

function getFilterOptionsAt(
  records: FlatSourceRecord[],
  definitions: FilterDefinition[],
  filters: Record<string, string[]>,
  index: number
) {
  const scopedDefinitions = definitions.slice(0, index);
  const relevantRecords = records.filter((record) =>
    matchesSelectedFilters(record, filters, scopedDefinitions)
  );

  return uniqueValues(
    relevantRecords
      .map((record) => record.dimensions[definitions[index].key])
      .filter((value): value is string => Boolean(value))
  );
}

function sameSource(left: Source, right: Source) {
  return sourceKey(left) === sourceKey(right);
}

function sameFilterOption(left: FilterOption, right: FilterOption) {
  return left.key === right.key && left.value === right.value;
}

function toFilterRecord(options: FilterOption[]): Record<string, string[]> {
  return options.reduce<Record<string, string[]>>((acc, option) => {
    const currentValues = acc[option.key] ?? [];
    if (!currentValues.includes(option.value)) {
      acc[option.key] = [...currentValues, option.value];
    }
    return acc;
  }, {});
}

function sanitizeFiltersByHierarchy(
  records: FlatSourceRecord[],
  definitions: FilterDefinition[],
  candidateFilters: Record<string, string[]>
) {
  const sanitizedFilters: Record<string, string[]> = {};

  definitions.forEach((definition, index) => {
    const requestedValues = candidateFilters[definition.key] ?? [];
    if (requestedValues.length === 0) {
      return;
    }

    const allowedOptions = getFilterOptionsAt(
      records,
      definitions,
      sanitizedFilters,
      index
    );

    const keptValues = requestedValues.filter((value) =>
      allowedOptions.includes(value)
    );

    if (keptValues.length > 0) {
      sanitizedFilters[definition.key] = keptValues;
    }
  });

  return sanitizedFilters;
}

function mergeSourceOptions(
  availableSources: Source[],
  selectedSources: Source[]
) {
  const mergedOptions: Source[] = [...availableSources];

  selectedSources.forEach((selectedSource) => {
    if (!mergedOptions.some((source) => sameSource(source, selectedSource))) {
      mergedOptions.push(selectedSource);
    }
  });

  return mergedOptions;
}

function getFilterOptionLabel(option: FilterOption) {
  return `${toFrenchGroupLabel(option.label, option.key)}: ${option.value}`;
}

function renderEllipsizedOption(
  props: React.HTMLAttributes<HTMLLIElement> & {
    key?: React.Key;
    ref?: React.Ref<HTMLLIElement>;
  },
  label: string
) {
  const { key, ref, ...rest } = props;
  const optionProps = { ...rest };

  return (
    <Box
      component="li"
      key={key}
      {...optionProps}
      ref={ref}
      sx={{
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <Tooltip title={label} placement="top-start">
        <span>{label}</span>
      </Tooltip>
    </Box>
  );
}

function sameSelections(
  left: Record<string, Source[]>,
  right: Record<string, Source[]>
) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((axisKey) => {
    const leftSources = left[axisKey] ?? [];
    const rightSources = right[axisKey] ?? [];

    if (leftSources.length !== rightSources.length) {
      return false;
    }

    return leftSources.every((leftSource, index) =>
      sameSource(leftSource, rightSources[index])
    );
  });
}

// --- component ---

interface Props {
  data: ApiDataSourceResponse;
  onAxisSelect?: (axisKey: string, selected: Source[]) => void;
  initialSelection?: Record<string, Source[]>;
  matchControlPanelStyle?: boolean;
}

export const V1LegacySelector: React.FC<Props> = ({
  data,
  onAxisSelect,
  initialSelection,
  matchControlPanelStyle = false,
}) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [selections, setSelections] = useState<Record<string, Source[]>>({});

  const flattenedData = useMemo(
    () => collectFlatSources(data.structure),
    [data.structure]
  );

  const activeFilterDefinitions = useMemo(
    () =>
      flattenedData.filters.filter((definition) => {
        const distinctValues = uniqueValues(
          flattenedData.records
            .map((record) => record.dimensions[definition.key])
            .filter((value): value is string => Boolean(value))
        );

        return distinctValues.length > 1;
      }),
    [flattenedData]
  );

  const groupOptions = useMemo(
    () =>
      activeFilterDefinitions.reduce<
        Record<string, { label: string; options: string[] }>
      >((acc, definition, index) => {
        acc[definition.key] = {
          label: definition.label,
          options: getFilterOptionsAt(
            flattenedData.records,
            activeFilterDefinitions,
            filters,
            index
          ),
        };
        return acc;
      }, {}),
    [activeFilterDefinitions, flattenedData.records, filters]
  );

  const allFilterOptions = useMemo(
    () =>
      activeFilterDefinitions.flatMap((definition) =>
        (groupOptions[definition.key]?.options ?? []).map((value) => ({
          key: definition.key,
          label: definition.label,
          value,
        }))
      ),
    [activeFilterDefinitions, groupOptions]
  );

  const selectedFilterOptions = useMemo(
    () =>
      activeFilterDefinitions.flatMap((definition) =>
        (filters[definition.key] ?? []).map((value) => ({
          key: definition.key,
          label: definition.label,
          value,
        }))
      ),
    [activeFilterDefinitions, filters]
  );

  const filteredRecords = useMemo(
    () =>
      flattenedData.records.filter((record) =>
        matchesSelectedFilters(record, filters, activeFilterDefinitions)
      ),
    [activeFilterDefinitions, flattenedData.records, filters]
  );

  const axisResult: AxisSourcesResult = useMemo(
    () => buildAxisSourcesResult(filteredRecords),
    [filteredRecords]
  );

  const availableSourcesByAxis = useMemo(
    () =>
      axisResult.kind === 'unique'
        ? { unique: axisResult.sources }
        : Object.fromEntries(
            Object.entries(axisResult.axes).map(([axisKey, axis]) => [
              axisKey,
              axis.options,
            ])
          ),
    [axisResult]
  );

  useEffect(() => {
    if (!initialSelection) {
      return;
    }

    const nextSelections = Object.entries(initialSelection).reduce<
      Record<string, Source[]>
    >((acc, [axisKey, selectedSources]) => {
      const normalizedSources = selectedSources
        .map(
          (selectedSource) =>
            (availableSourcesByAxis[axisKey] ?? []).find((availableSource) =>
              sameSource(availableSource, selectedSource)
            ) ?? selectedSource
        )
        .filter((source): source is Source => Boolean(source));

      if (normalizedSources.length > 0) {
        acc[axisKey] = normalizedSources;
      }

      return acc;
    }, {});

    setSelections((currentSelections) =>
      sameSelections(currentSelections, nextSelections)
        ? currentSelections
        : nextSelections
    );
  }, [initialSelection, availableSourcesByAxis]);

  const handleFiltersChange = (selectedOptions: FilterOption[]) => {
    const candidateFilters = toFilterRecord(selectedOptions);
    const nextFilters = sanitizeFiltersByHierarchy(
      flattenedData.records,
      activeFilterDefinitions,
      candidateFilters
    );
    setFilters(nextFilters);
  };

  const hasVisibleFilters = activeFilterDefinitions.length > 0;

  const renderFilterTags = (
    selectedOptions: FilterOption[],
    getTagProps: (params: { index: number }) => Record<string, unknown>
  ) =>
    selectedOptions.map((option, index) => {
      const label = getFilterOptionLabel(option);
      const tagProps = getTagProps({ index });
      const key = String(
        tagProps.key ?? `${option.key}:${option.value}:${index}`
      );

      return (
        <Tooltip key={key} title={label} placement="top-start">
          <Chip
            {...tagProps}
            label={label}
            size="small"
            sx={{ maxWidth: 260 }}
          />
        </Tooltip>
      );
    });

  const renderSourceTags = (
    selectedSources: Source[],
    getTagProps: (params: { index: number }) => Record<string, unknown>
  ) =>
    selectedSources.map((source, index) => {
      const label = source.label;
      const tagProps = getTagProps({ index });
      const key = String(tagProps.key ?? sourceKey(source));

      return (
        <Tooltip key={key} title={label} placement="top">
          <Chip
            {...tagProps}
            label={label}
            size="small"
            sx={{ maxWidth: 260 }}
          />
        </Tooltip>
      );
    });

  const handleAxisSelect = (axisKey: string, selected: Source[]) => {
    const newSelections = { ...selections, [axisKey]: selected };
    setSelections(newSelections);
    onAxisSelect?.(axisKey, selected);
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      p={matchControlPanelStyle ? 2 : 0}
      width={matchControlPanelStyle ? 300 : undefined}
    >
      {matchControlPanelStyle ? (
        <>
          <Typography
            variant="subtitle2"
            sx={{ fontSize: '0.9rem', fontWeight: 700 }}
          >
            Sources de données
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              bgcolor: 'background.paper',
              px: 1.5,
              py: 0.75,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '& .MuiInputBase-root': {
                minHeight: 36,
              },
              '& .MuiOutlinedInput-input': {
                py: 0.75,
              },
              '& .MuiAutocomplete-tag': {
                height: 22,
              },
            }}
          >
            {/* -- hierarchical filters in a single grouped selector */}
            {hasVisibleFilters && (
              <Autocomplete
                multiple
                options={allFilterOptions}
                value={selectedFilterOptions}
                onChange={(_, values) => handleFiltersChange(values)}
                isOptionEqualToValue={sameFilterOption}
                groupBy={(option) =>
                  toFrenchGroupLabel(option.label, option.key)
                }
                getOptionLabel={getFilterOptionLabel}
                renderTags={renderFilterTags}
                renderOption={(props, option) =>
                  renderEllipsizedOption(props, getFilterOptionLabel(option))
                }
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Filtres" size="small" />
                )}
              />
            )}

            {/* -- sources by axis -- */}
            {axisResult.kind === 'unique' && (
              <Autocomplete
                multiple
                options={mergeSourceOptions(
                  axisResult.sources,
                  selections['unique'] ?? []
                )}
                value={selections['unique'] ?? []}
                getOptionLabel={(src) => src.label}
                renderTags={renderSourceTags}
                renderOption={(props, source) =>
                  renderEllipsizedOption(props, source.label)
                }
                isOptionEqualToValue={sameSource}
                onChange={(_, values) => handleAxisSelect('unique', values)}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Sources" size="small" />
                )}
              />
            )}

            {axisResult.kind === 'axes' && (
              <>
                {Object.entries(axisResult.axes).map(([axisKey, sources]) => (
                  <Autocomplete
                    key={axisKey}
                    multiple
                    options={mergeSourceOptions(
                      sources.options,
                      selections[axisKey] ?? []
                    )}
                    value={selections[axisKey] ?? []}
                    getOptionLabel={(src) => src.label}
                    renderTags={renderSourceTags}
                    renderOption={(props, source) =>
                      renderEllipsizedOption(props, source.label)
                    }
                    isOptionEqualToValue={sameSource}
                    onChange={(_, values) => handleAxisSelect(axisKey, values)}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={sources.label}
                        size="small"
                      />
                    )}
                  />
                ))}

                {'unassigned' in axisResult.axes && (
                  <Alert severity="warning">
                    Problème de configuration : certaines sources, sans axe
                    assigné, ont été ignorées.
                  </Alert>
                )}
              </>
            )}
          </Box>
        </>
      ) : (
        <>
          {/* -- hierarchical filters in a single grouped selector */}
          {hasVisibleFilters && (
            <Autocomplete
              multiple
              options={allFilterOptions}
              value={selectedFilterOptions}
              onChange={(_, values) => handleFiltersChange(values)}
              isOptionEqualToValue={sameFilterOption}
              groupBy={(option) => toFrenchGroupLabel(option.label, option.key)}
              getOptionLabel={getFilterOptionLabel}
              renderTags={renderFilterTags}
              renderOption={(props, option) =>
                renderEllipsizedOption(props, getFilterOptionLabel(option))
              }
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Filtres" size="small" />
              )}
            />
          )}

          {axisResult.kind === 'unique' && (
            <Autocomplete
              multiple
              options={mergeSourceOptions(
                axisResult.sources,
                selections['unique'] ?? []
              )}
              value={selections['unique'] ?? []}
              getOptionLabel={(src) => src.label}
              renderTags={renderSourceTags}
              renderOption={(props, source) =>
                renderEllipsizedOption(props, source.label)
              }
              isOptionEqualToValue={sameSource}
              onChange={(_, values) => handleAxisSelect('unique', values)}
              size="small"
              renderInput={(params) => (
                <TextField {...params} label="Sources" size="small" />
              )}
            />
          )}

          {axisResult.kind === 'axes' && (
            <>
              {Object.entries(axisResult.axes).map(([axisKey, sources]) => (
                <Autocomplete
                  key={axisKey}
                  multiple
                  options={mergeSourceOptions(
                    sources.options,
                    selections[axisKey] ?? []
                  )}
                  value={selections[axisKey] ?? []}
                  getOptionLabel={(src) => src.label}
                  renderTags={renderSourceTags}
                  renderOption={(props, source) =>
                    renderEllipsizedOption(props, source.label)
                  }
                  isOptionEqualToValue={sameSource}
                  onChange={(_, values) => handleAxisSelect(axisKey, values)}
                  size="small"
                  renderInput={(params) => (
                    <TextField {...params} label={sources.label} size="small" />
                  )}
                />
              ))}

              {'unassigned' in axisResult.axes && (
                <Alert severity="warning">
                  Problème de configuration : certaines sources, sans axe
                  assigné, ont été ignorées.
                </Alert>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

/** @deprecated Use {@link WidgetSourceSelector} with {@link useFetchDataSources} instead. */
export const SourceSelectorFlat = V1LegacySelector;
